/** Shared grid placement helpers (extracted from DashboardPage). */

export const ROW_PX = 80;
export const GRID_GAP_PX = 16;
export const GRID_TRACKS = 12;
export const MAX_ROW_SPAN = 24;

export function clampInt(n, lo, hi, fallback) {
  const x = Math.round(Number(n));
  if (!Number.isFinite(x)) return fallback;
  return Math.min(hi, Math.max(lo, x));
}

export function rectsOverlap(a, b) {
  return (
    a.col < b.col + b.w
    && b.col < a.col + a.w
    && a.row < b.row + b.h
    && b.row < a.row + a.h
  );
}

export function placementCollides(p, others) {
  return others.some((o) => rectsOverlap(p, o));
}

export function findFirstFreeSlot(w, h, placedRects) {
  const maxOccupiedRow = placedRects.reduce((m, r) => Math.max(m, r.row + r.h - 1), 0);
  for (let row = 1; row <= maxOccupiedRow + 1; row += 1) {
    for (let col = 1; col + w - 1 <= GRID_TRACKS; col += 1) {
      const candidate = { col, row, w, h };
      if (!placementCollides(candidate, placedRects)) return candidate;
    }
  }
  return { col: 1, row: maxOccupiedRow + 1, w, h };
}

export function resolveDrop(sizes, draggedId, oldPlacement, target) {
  const overlapping = Object.entries(sizes)
    .filter(([id, p]) => id !== draggedId && p && rectsOverlap(p, target));

  if (overlapping.length === 0) {
    return { ...sizes, [draggedId]: target };
  }

  if (overlapping.length === 1 && oldPlacement) {
    const [otherId, otherP] = overlapping[0];
    if (otherP.w === oldPlacement.w && otherP.h === oldPlacement.h) {
      const swapTarget = { ...otherP, col: oldPlacement.col, row: oldPlacement.row };
      const others = Object.entries(sizes)
        .filter(([id]) => id !== draggedId && id !== otherId)
        .map(([, p]) => p);
      if (!placementCollides(swapTarget, others)) {
        return { ...sizes, [draggedId]: target, [otherId]: swapTarget };
      }
    }
  }

  const result = {};
  Object.entries(sizes).forEach(([id, p]) => {
    if (id === draggedId) return;
    if (overlapping.some(([oid]) => oid === id)) return;
    result[id] = p;
  });
  result[draggedId] = target;
  overlapping.forEach(([id, p]) => {
    const placed = Object.values(result);
    result[id] = findFirstFreeSlot(p.w, p.h, placed);
  });
  return result;
}

export function generateClientId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `w-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
}

export function emptyLayout() {
  return { order: [], sizes: {}, hidden: [] };
}

export function createGridTypeHelpers(getWidgetMeta) {
  function minWForType(type) {
    const m = getWidgetMeta(type);
    const raw = m?.minSize?.w ?? m?.minGridSpan ?? 12;
    return clampInt(raw, 1, GRID_TRACKS, 1);
  }

  function minHForType(type) {
    const raw = getWidgetMeta(type)?.minSize?.h ?? 1;
    return clampInt(raw, 1, MAX_ROW_SPAN, 1);
  }

  function defaultWForType(type) {
    const m = getWidgetMeta(type);
    const raw = m?.defaultSize?.w ?? m?.defaultGridSpan ?? 12;
    return clampInt(raw, minWForType(type), GRID_TRACKS, 12);
  }

  function defaultHForType(type) {
    const m = getWidgetMeta(type);
    const raw = m?.defaultSize?.h ?? m?.gridRowSpan ?? 4;
    return clampInt(raw, minHForType(type), MAX_ROW_SPAN, 4);
  }

  function clampPlacement(p, type) {
    const minW = minWForType(type);
    const minH = minHForType(type);
    const w = clampInt(p?.w, minW, GRID_TRACKS, defaultWForType(type));
    const h = clampInt(p?.h, minH, MAX_ROW_SPAN, defaultHForType(type));
    const col = clampInt(p?.col, 1, GRID_TRACKS - w + 1, 1);
    const row = clampInt(p?.row, 1, 500, 1);
    return { col, row, w, h };
  }

  function getWidgetPlacement(layout, id, type) {
    const stored = layout?.sizes?.[id];
    if (stored
        && typeof stored.col === 'number'
        && typeof stored.row === 'number'
        && typeof stored.w === 'number'
        && typeof stored.h === 'number') {
      return clampPlacement(stored, type);
    }
    return clampPlacement({ col: 1, row: 1, w: defaultWForType(type), h: defaultHForType(type) }, type);
  }

  return {
    minWForType,
    minHForType,
    defaultWForType,
    defaultHForType,
    clampPlacement,
    getWidgetPlacement,
  };
}

export function normalizeSimpleLayout(dashboard, getWidgetMeta) {
  if (!dashboard) return dashboard;
  const { clampPlacement, defaultWForType, defaultHForType, getWidgetPlacement } = createGridTypeHelpers(getWidgetMeta);
  const widgets = Array.isArray(dashboard.widgets) ? dashboard.widgets : [];
  const widgetIds = new Set(widgets.map((w) => w.id));
  const rawLayout = dashboard.layout || emptyLayout();
  const order = Array.isArray(rawLayout.order)
    ? rawLayout.order.filter((id) => widgetIds.has(id))
    : [];
  const hidden = Array.isArray(rawLayout.hidden)
    ? rawLayout.hidden.filter((id) => widgetIds.has(id))
    : [];
  const sizes = {};
  order.forEach((id) => {
    const type = widgets.find((w) => w.id === id)?.type || '';
    sizes[id] = getWidgetPlacement({ sizes: rawLayout.sizes }, id, type);
  });
  widgets.forEach((w) => {
    if (!order.includes(w.id)) {
      order.push(w.id);
      sizes[w.id] = clampPlacement(
        { col: 1, row: order.length, w: defaultWForType(w.type), h: defaultHForType(w.type) },
        w.type,
      );
    }
  });
  return { ...dashboard, layout: { order, sizes, hidden } };
}
